import { useState } from 'react';
import { ContratosService } from '@/services/contratosService';
import { ContratoWithDetails, PropostaParaContrato } from '@/types/contrato';
import { DadosContrato } from '@/utils/contratoUtils';
import { 
  criarDadosIniciaisContrato, 
  preencherDadosDeProposta, 
  preencherDadosCompletosDeProposta,
  preencherDadosDeContratoExistente,
  calcularValorParcela,
  calcularValoresIntegracao,
  validarCamposObrigatorios,
  validarPercentuaisIntegracao,
  detectarMudancasContrato
} from '@/utils/contratoUtils';
import { toast } from 'sonner';

export function useContratoForm() {
  const [dadosContrato, setDadosContrato] = useState<DadosContrato>(criarDadosIniciaisContrato());
  const [dadosOriginais, setDadosOriginais] = useState<DadosContrato>(criarDadosIniciaisContrato());
  const [loadingAction, setLoadingAction] = useState(false);

  // Função auxiliar para remover campos undefined, null e strings vazias antes de enviar ao banco
  const limparUndefined = (obj: any) => {
    const cleaned: any = {};
    for (const key in obj) {
      const value = obj[key];
      // Remover undefined, null e strings vazias
      if (value !== undefined && value !== null && value !== '') {
        cleaned[key] = value;
      }
    }
    return cleaned;
  };

  // Função para atualizar valores do kit (valor digitado pelo usuário é o valor real do contrato)
  const atualizarValoresKit = (formaPagamento: string, valorKit: number) => {
    let numeroParcelas = dadosContrato.kit_numero_parcelas || 1;
    let valorParcela = dadosContrato.kit_valor_parcela || valorKit;

    // Para cartão de crédito, manter o valor da parcela atual (inserido manualmente)
    // Para outras formas de pagamento, o valor da parcela é igual ao valor total
    if (formaPagamento !== 'cartao_credito') {
      numeroParcelas = 1;
      valorParcela = valorKit;
    }

    setDadosContrato(prev => ({
      ...prev,
      kit_valor_pagamento: valorKit,
      valor_kit: valorKit,
      valor_total: valorKit + prev.valor_integracao,
      kit_numero_parcelas: numeroParcelas,
      kit_valor_parcela: valorParcela
    }));
  };

  // Função para atualizar valores da integração
  const atualizarValoresIntegracao = (modalidade: number, valorTotal: number) => {
    // Definir percentuais padrão baseado na modalidade
    let percentuais = {
      integracao_percentual_assinatura: dadosContrato.integracao_percentual_assinatura,
      integracao_percentual_inicio_obra: dadosContrato.integracao_percentual_inicio_obra,
      integracao_percentual_conclusao_obra: dadosContrato.integracao_percentual_conclusao_obra,
      integracao_percentual_ligacao_definitiva: dadosContrato.integracao_percentual_ligacao_definitiva
    };

    // Aplicar percentuais pré-definidos baseado na modalidade
    switch (modalidade) {
      case 2: // Assinatura + Conclusão da obra
        percentuais = {
          integracao_percentual_assinatura: 50,
          integracao_percentual_inicio_obra: 0,
          integracao_percentual_conclusao_obra: 50,
          integracao_percentual_ligacao_definitiva: 0
        };
        break;
      case 3: // Assinatura + Início + Conclusão da obra
        percentuais = {
          integracao_percentual_assinatura: 30,
          integracao_percentual_inicio_obra: 35,
          integracao_percentual_conclusao_obra: 35,
          integracao_percentual_ligacao_definitiva: 0
        };
        break;
      case 1: // Pagamento integral na assinatura
        percentuais = {
          integracao_percentual_assinatura: 100,
          integracao_percentual_inicio_obra: 0,
          integracao_percentual_conclusao_obra: 0,
          integracao_percentual_ligacao_definitiva: 0
        };
        break;
      case 4: // Assinatura + Início + Conclusão + Ligação definitiva
        percentuais = {
          integracao_percentual_assinatura: 20,
          integracao_percentual_inicio_obra: 30,
          integracao_percentual_conclusao_obra: 35,
          integracao_percentual_ligacao_definitiva: 5
        };
        break;
      case 5: // Pagamento integral na conclusão da obra
        percentuais = {
          integracao_percentual_assinatura: 0,
          integracao_percentual_inicio_obra: 0,
          integracao_percentual_conclusao_obra: 100,
          integracao_percentual_ligacao_definitiva: 0
        };
        break;
    }

    const valores = calcularValoresIntegracao(modalidade, valorTotal, percentuais);
    
    // Resetar parcelas quando modalidade muda
    const numeroParcelas = 1;
    const valorParcela = valorTotal;

    setDadosContrato(prev => ({
      ...prev,
      integracao_modalidade_pagamento: modalidade,
      integracao_valor_total: valorTotal,
      valor_integracao: valorTotal,
      valor_total: prev.valor_kit + valorTotal,
      integracao_numero_parcelas: numeroParcelas,
      integracao_valor_parcela: valorParcela,
      ...percentuais,
      ...valores
    }));
  };

  const validarCampos = (tipoCliente?: string) => {
    const camposObrigatoriosOk = validarCamposObrigatorios(dadosContrato, tipoCliente);
    // Não bloquear salvamento por causa dos percentuais, apenas avisar visualmente
    return camposObrigatoriosOk;
  };

  const obterMensagemValidacao = (tipoCliente?: string) => {
    // Não retornar mensagem de validação de percentuais aqui
    // A mensagem de aviso já está sendo exibida no componente
    return null;
  };

  const salvarContrato = async (propostaSelecionada: PropostaParaContrato) => {
    try {
      setLoadingAction(true);
      
      const { kit_forma_pagamento, integracao_forma_pagamento, integracao_modalidade_pagamento, integracao_forma_pagamento_assinatura, integracao_forma_pagamento_inicio_obra, integracao_forma_pagamento_conclusao_obra, integracao_forma_pagamento_ligacao_definitiva, integracao_numero_parcelas_assinatura, integracao_valor_parcela_assinatura, integracao_numero_parcelas_inicio_obra, integracao_valor_parcela_inicio_obra, integracao_numero_parcelas_conclusao_obra, integracao_valor_parcela_conclusao_obra, integracao_numero_parcelas_ligacao_definitiva, integracao_valor_parcela_ligacao_definitiva, ...dadosSemTiposEspeciais } = dadosContrato;

      // Valores reais do contrato: usar os digitados pelo usuário (podem divergir da proposta)
      const valorKitContrato = dadosContrato.kit_valor_pagamento;
      const valorIntegracaoContrato = dadosContrato.integracao_valor_total;
      const valorTotalContrato = valorKitContrato + valorIntegracaoContrato;

      const contratoData = limparUndefined({
        lead_id: propostaSelecionada.lead_id,
        ...dadosSemTiposEspeciais,
        valor_kit: valorKitContrato,
        valor_integracao: valorIntegracaoContrato,
        valor_total: valorTotalContrato,
        kit_forma_pagamento: kit_forma_pagamento as 'boleto' | 'pix' | 'cartao_credito' | 'cartao_debito' | 'financiamento_bancario' | undefined,
        integracao_forma_pagamento: integracao_forma_pagamento as 'boleto' | 'pix' | 'cartao_credito' | 'cartao_debito' | 'financiamento_bancario' | undefined, // Mantido para compatibilidade
        integracao_modalidade_pagamento: integracao_modalidade_pagamento as 1 | 2 | 3 | 4 | 5 | undefined,
        integracao_forma_pagamento_assinatura: integracao_forma_pagamento_assinatura as 'boleto' | 'pix' | 'cartao_credito' | 'cartao_debito' | 'financiamento_bancario' | undefined,
        integracao_forma_pagamento_inicio_obra: integracao_forma_pagamento_inicio_obra as 'boleto' | 'pix' | 'cartao_credito' | 'cartao_debito' | 'financiamento_bancario' | undefined,
        integracao_forma_pagamento_conclusao_obra: integracao_forma_pagamento_conclusao_obra as 'boleto' | 'pix' | 'cartao_credito' | 'cartao_debito' | 'financiamento_bancario' | undefined,
        integracao_forma_pagamento_ligacao_definitiva: integracao_forma_pagamento_ligacao_definitiva as 'boleto' | 'pix' | 'cartao_credito' | 'cartao_debito' | 'financiamento_bancario' | undefined,
        integracao_numero_parcelas_assinatura: integracao_numero_parcelas_assinatura,
        integracao_valor_parcela_assinatura: integracao_valor_parcela_assinatura,
        integracao_numero_parcelas_inicio_obra: integracao_numero_parcelas_inicio_obra,
        integracao_valor_parcela_inicio_obra: integracao_valor_parcela_inicio_obra,
        integracao_numero_parcelas_conclusao_obra: integracao_numero_parcelas_conclusao_obra,
        integracao_valor_parcela_conclusao_obra: integracao_valor_parcela_conclusao_obra,
        integracao_numero_parcelas_ligacao_definitiva: integracao_numero_parcelas_ligacao_definitiva,
        integracao_valor_parcela_ligacao_definitiva: integracao_valor_parcela_ligacao_definitiva,
        potencia_total_paineis: propostaSelecionada.potencia_total_paineis,
        potencia_total_inversores: propostaSelecionada.potencia_total_inversores,
        total_paineis: propostaSelecionada.total_paineis
      });

      const contrato = await ContratosService.criarContrato(propostaSelecionada.id, contratoData);
      toast.success('Contrato criado com sucesso!');
      return contrato;
    } catch (error) {
      console.error('Erro ao salvar contrato:', error);
      toast.error('Erro ao salvar contrato');
      throw error;
    } finally {
      setLoadingAction(false);
    }
  };

  const salvarEdicaoContrato = async (contratoEditando: ContratoWithDetails) => {
    try {
      setLoadingAction(true);
      
      const { kit_forma_pagamento, integracao_forma_pagamento, integracao_modalidade_pagamento, integracao_forma_pagamento_assinatura, integracao_forma_pagamento_inicio_obra, integracao_forma_pagamento_conclusao_obra, integracao_forma_pagamento_ligacao_definitiva, integracao_numero_parcelas_assinatura, integracao_valor_parcela_assinatura, integracao_numero_parcelas_inicio_obra, integracao_valor_parcela_inicio_obra, integracao_numero_parcelas_conclusao_obra, integracao_valor_parcela_conclusao_obra, integracao_numero_parcelas_ligacao_definitiva, integracao_valor_parcela_ligacao_definitiva, ...dadosSemTiposEspeciais } = dadosContrato;

      const contratoAtualizado = await ContratosService.atualizarContrato(contratoEditando.id, limparUndefined({
        ...dadosSemTiposEspeciais,
        kit_forma_pagamento: kit_forma_pagamento as 'boleto' | 'pix' | 'cartao_credito' | 'cartao_debito' | 'financiamento_bancario' | undefined,
        integracao_forma_pagamento: integracao_forma_pagamento as 'boleto' | 'pix' | 'cartao_credito' | 'cartao_debito' | 'financiamento_bancario' | undefined, // Mantido para compatibilidade
        integracao_modalidade_pagamento: integracao_modalidade_pagamento as 1 | 2 | 3 | 4 | 5 | undefined,
        integracao_forma_pagamento_assinatura: integracao_forma_pagamento_assinatura as 'boleto' | 'pix' | 'cartao_credito' | 'cartao_debito' | 'financiamento_bancario' | undefined,
        integracao_forma_pagamento_inicio_obra: integracao_forma_pagamento_inicio_obra as 'boleto' | 'pix' | 'cartao_credito' | 'cartao_debito' | 'financiamento_bancario' | undefined,
        integracao_forma_pagamento_conclusao_obra: integracao_forma_pagamento_conclusao_obra as 'boleto' | 'pix' | 'cartao_credito' | 'cartao_debito' | 'financiamento_bancario' | undefined,
        integracao_forma_pagamento_ligacao_definitiva: integracao_forma_pagamento_ligacao_definitiva as 'boleto' | 'pix' | 'cartao_credito' | 'cartao_debito' | 'financiamento_bancario' | undefined,
        integracao_numero_parcelas_assinatura: integracao_numero_parcelas_assinatura,
        integracao_valor_parcela_assinatura: integracao_valor_parcela_assinatura,
        integracao_numero_parcelas_inicio_obra: integracao_numero_parcelas_inicio_obra,
        integracao_valor_parcela_inicio_obra: integracao_valor_parcela_inicio_obra,
        integracao_numero_parcelas_conclusao_obra: integracao_numero_parcelas_conclusao_obra,
        integracao_valor_parcela_conclusao_obra: integracao_valor_parcela_conclusao_obra,
        integracao_numero_parcelas_ligacao_definitiva: integracao_numero_parcelas_ligacao_definitiva,
        integracao_valor_parcela_ligacao_definitiva: integracao_valor_parcela_ligacao_definitiva,
        valor_total: dadosContrato.valor_kit + dadosContrato.valor_integracao
      }));
      
      toast.success('Contrato atualizado com sucesso!');
      return contratoAtualizado;
    } catch (error) {
      console.error('Erro ao atualizar contrato:', error);
      toast.error('Erro ao atualizar contrato');
      throw error;
    } finally {
      setLoadingAction(false);
    }
  };

  const preencherDadosIniciais = (proposta: PropostaParaContrato) => {
    setDadosContrato(prev => preencherDadosDeProposta(proposta, prev));
  };

  const preencherDadosCompletos = (proposta: PropostaParaContrato) => {
    setDadosContrato(prev => preencherDadosCompletosDeProposta(proposta, prev));
  };

  const preencherDadosParaEdicao = (contrato: ContratoWithDetails) => {
    const dadosPreenchidos = preencherDadosDeContratoExistente(contrato);
    setDadosContrato(dadosPreenchidos);
    setDadosOriginais(dadosPreenchidos); // Armazenar dados originais para comparação
  };

  const resetarFormulario = () => {
    const dadosIniciais = criarDadosIniciaisContrato();
    setDadosContrato(dadosIniciais);
    setDadosOriginais(dadosIniciais);
  };

  // Função para detectar se houve mudanças
  const temMudancas = () => {
    return detectarMudancasContrato(dadosOriginais, dadosContrato);
  };

  return {
    dadosContrato,
    setDadosContrato,
    loadingAction,
    atualizarValoresKit,
    atualizarValoresIntegracao,
    validarCampos,
    obterMensagemValidacao,
    salvarContrato,
    salvarEdicaoContrato,
    preencherDadosIniciais,
    preencherDadosCompletos,
    preencherDadosParaEdicao,
    resetarFormulario,
    calcularValorParcela,
    temMudancas
  };
}
